import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import type { ReactNode } from "react";
import { AppShell, type SidebarNavItem } from "@qr-menu/ui";
import { LoginRoute } from "./routes/Login";
import { RegisterRoute } from "./routes/Register";
import { ForgotPasswordRoute } from "./routes/ForgotPassword";
import { ResetPasswordRoute } from "./routes/ResetPassword";
import { VerifyEmailRoute } from "./routes/VerifyEmail";
import { AcceptInvitationRoute } from "./routes/AcceptInvitation";
import { HomeRoute } from "./routes/Home";
import { CategoriesRoute } from "./routes/Categories";
import { ProductsRoute } from "./routes/Products";
import { MenuImportRoute } from "./routes/MenuImport";
import { AccountRoute } from "./routes/Account";
import { TeamRoute } from "./routes/Team";
import { BusinessRoute } from "./routes/Business";
import { AppearanceRoute } from "./routes/Appearance";
import { clearTenantToken, readTenantToken } from "./lib/session";

// Kimliksiz yüzeyler: bu route'larda AppShell chrome'u (sidebar/topbar) hiç
// render edilmez, tıpkı platform-panel'in giriş ekranında olduğu gibi.
const PUBLIC_PATHS = new Set([
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/verify-email",
  "/accept-invitation",
]);

function RequireSession({ children }: { children: ReactNode }) {
  if (!readTenantToken()) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

const navItems: SidebarNavItem[] = [
  { to: "/app", label: "Genel Bakış", end: true },
  { to: "/app/categories", label: "Kategoriler" },
  { to: "/app/products", label: "Ürünler" },
  { to: "/app/import", label: "İçe Aktar" },
  { to: "/app/team", label: "Ekip" },
  { to: "/app/business", label: "İşletme" },
  { to: "/app/appearance", label: "Görünüm" },
  { to: "/app/account", label: "Hesap" },
];

function Shell({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();

  if (PUBLIC_PATHS.has(location.pathname)) {
    return <>{children}</>;
  }

  return (
    <AppShell
      brandName="qr-menu"
      brandTag="Tenant"
      navItems={navItems}
      sidebarFooter={
        <button
          type="button"
          className="qr-sidebar-action"
          onClick={() => {
            clearTenantToken();
            navigate("/login", { replace: true });
          }}
        >
          Oturumu kapat
        </button>
      }
    >
      {children}
    </AppShell>
  );
}

export function App() {
  return (
    <Shell>
      <Routes>
        <Route path="/" element={<Navigate to="/app" replace />} />
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/register" element={<RegisterRoute />} />
        <Route path="/forgot-password" element={<ForgotPasswordRoute />} />
        <Route path="/reset-password" element={<ResetPasswordRoute />} />
        <Route path="/verify-email" element={<VerifyEmailRoute />} />
        <Route
          path="/accept-invitation"
          element={<AcceptInvitationRoute />}
        />
        <Route
          path="/app"
          element={
            <RequireSession>
              <HomeRoute />
            </RequireSession>
          }
        />
        <Route
          path="/app/categories"
          element={
            <RequireSession>
              <CategoriesRoute />
            </RequireSession>
          }
        />
        <Route
          path="/app/products"
          element={
            <RequireSession>
              <ProductsRoute />
            </RequireSession>
          }
        />
        <Route
          path="/app/import"
          element={
            <RequireSession>
              <MenuImportRoute />
            </RequireSession>
          }
        />
        <Route
          path="/app/team"
          element={
            <RequireSession>
              <TeamRoute />
            </RequireSession>
          }
        />
        <Route
          path="/app/business"
          element={
            <RequireSession>
              <BusinessRoute />
            </RequireSession>
          }
        />
        <Route
          path="/app/appearance"
          element={
            <RequireSession>
              <AppearanceRoute />
            </RequireSession>
          }
        />
        <Route
          path="/app/account"
          element={
            <RequireSession>
              <AccountRoute />
            </RequireSession>
          }
        />
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Routes>
    </Shell>
  );
}
